<script lang="ts">
  import ChartCard from '../lib/charts/ChartCard.svelte';
  import Chart from '../lib/charts/Chart.svelte';
  import { dimensionColor } from '../lib/charts/dimension-colors';
  import { toCompositionOption, toTimeChartOption } from '../lib/charts/options';
  import { queryStock } from '../lib/analytics/stock';
  import type { DataStore } from '../lib/data/store';
  import type { Manifest, StockQuery, YearMonth } from '../lib/data/types';
  import FilterBar from '../lib/filters/FilterBar.svelte';
  import { formatCents, formatMonth, formatPercent, signedClass } from '../lib/presentation/format';
  import {
    isLiabilityGroup,
    meaningfulChange,
    selectedAccountMonths,
  } from '../lib/presentation/insights';
  import { labelStockSeries } from '../lib/presentation/series';
  import type { AppState } from '../lib/state/app.svelte';
  import Icon from '../lib/ui/Icon.svelte';
  import KpiCard from '../lib/ui/KpiCard.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';

  let { store, manifest, state }: { store: DataStore; manifest: Manifest; state: AppState } =
    $props();

  let stockQuery = $derived.by((): StockQuery => ({
    metric: 'closing-balance',
    breakdown: 'none',
    visiblePeriod: {
      from: state.fromMonth ?? store.months[0]!,
      to: state.toMonth ?? store.months.at(-1)!,
    },
    ...(state.accountFilterActive ? { accountIds: new Set(state.accountIds) } : {}),
  }));
  let trend = $derived(
    labelStockSeries(
      queryStock(store.core.account_month, store.months, stockQuery),
      stockQuery,
      store,
    ),
  );
  let trendValues = $derived(trend.series[0]?.values ?? []);
  let current = $derived(trendValues.at(-1) ?? 0);
  let previous = $derived(trendValues.at(-2) ?? current);
  let delta = $derived(current - previous);
  let deltaPercent = $derived(meaningfulChange(current, previous));
  let finalMonth = $derived((trend.buckets.at(-1) ?? store.months.at(-1))! as YearMonth);
  let finalRows = $derived(
    selectedAccountMonths(
      store,
      finalMonth,
      state.accountFilterActive ? state.accountIds : undefined,
    ),
  );
  let assets = $derived(
    finalRows
      .filter((row) => !isLiabilityGroup(store, row.account_group_id))
      .reduce((sum, row) => sum + row.closing_balance_cents, 0),
  );
  let liabilities = $derived(
    finalRows
      .filter((row) => isLiabilityGroup(store, row.account_group_id))
      .reduce((sum, row) => sum + row.closing_balance_cents, 0),
  );
  let groupComposition = $derived.by(() => {
    const rows = new Map<string, number>();
    for (const row of finalRows)
      rows.set(
        row.account_group_id,
        (rows.get(row.account_group_id) ?? 0) + row.closing_balance_cents,
      );
    return Array.from(rows, ([id, value]) => ({
      name: store.groupLabel(id),
      value,
      colorKey: `group:${id}`,
    }))
      .filter((row) => row.value !== 0)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  });
  let contributors = $derived(
    [...finalRows].sort((a, b) => Math.abs(b.delta_cents) - Math.abs(a.delta_cents)).slice(0, 5),
  );
  let maxContribution = $derived(
    Math.max(1, ...contributors.map((row) => Math.abs(row.delta_cents))),
  );

  function go(route: 'accounts'): void {
    state.navigate(route);
  }
</script>

<main id="main-content" class="page">
  <PageHeader
    title="Il tuo patrimonio"
    description={`Situazione aggiornata al ${new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium' }).format(new Date(manifest.generated_at))}.`}
    {state}
  />
  <FilterBar {state} />

  <div class="kpi-grid">
    <KpiCard
      hero
      label={`Net worth · ${formatMonth(finalMonth)}`}
      value={formatCents(current)}
      detail="Attività meno passività"
    />
    <KpiCard
      label="Variazione mensile"
      value={formatCents(delta)}
      detail={formatPercent(deltaPercent)}
      tone={signedClass(delta)}
    />
    <KpiCard
      label="Attività"
      value={formatCents(assets)}
      detail={`${finalRows.filter((row) => !isLiabilityGroup(store, row.account_group_id)).length} conti`}
      tone="positive"
    />
    <KpiCard
      label="Passività"
      value={formatCents(liabilities)}
      detail={`${finalRows.filter((row) => isLiabilityGroup(store, row.account_group_id)).length} conti`}
      tone={liabilities < 0 ? 'negative' : 'neutral'}
    />
  </div>

  <div class="page-grid">
    <div class="span-8">
      <ChartCard
        title="Andamento del patrimonio"
        description="Saldo mensile complessivo dei conti selezionati."
        result={trend}
        option={toTimeChartOption(trend, 'area')}
        height="21rem"
      />
    </div>
    <section class="card list-card span-4">
      <header class="list-header">
        <h2>Composizione</h2>
        <p class="muted">Per gruppo a {formatMonth(finalMonth)}</p>
      </header>
      {#if groupComposition.length}
        <div style="padding: 0 .8rem 1rem">
          <Chart
            option={toCompositionOption(groupComposition)}
            ariaLabel="Composizione del patrimonio per gruppo"
            height="17rem"
          />
        </div>
      {/if}
    </section>

    <section class="card list-card span-12">
      <header class="list-header">
        <h2>Principali variazioni</h2>
        <p class="muted">Contributo nel mese finale</p>
      </header>
      <ul class="composition-list">
        {#each contributors as row}
          <li
            class="dimension-row"
            style={`--dimension-color: ${dimensionColor(`account:${row.account_id}`, store.accountLabel(row.account_id))}`}
          >
            <span class="dimension-label">{store.accountLabel(row.account_id)}</span>
            <strong class={signedClass(row.delta_cents)}>{formatCents(row.delta_cents)}</strong>
            <div class="progress">
              <span style:width={`${(Math.abs(row.delta_cents) / maxContribution) * 100}%`}></span>
            </div>
          </li>
        {/each}
      </ul>
      <div style="padding: .5rem 1.3rem">
        <button class="link-row text-button" type="button" onclick={() => go('accounts')}
          >Vedi tutti gli account <Icon name="chevron-right" size={17} /></button
        >
      </div>
    </section>
  </div>
</main>
